// Funnel (/staff/funnel, item 30): where this week's card requests came from, then visit, signup, card started, card sent and
// call by channel, every day and who is stuck. A rate shows only when both sides cover the same days. There is one funnel:
// page types and the email detail fold up at the bottom here, and Outreach links to this page.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../../../lib'
import { Busy, Button, Err, Fresh, Page, Section, Sources, Strip, Table, ago } from '../kit'
import type { Num } from '../../growth/types'
import { n, pct, short, sum } from '../../growth/format'
import { funnelColumns, range, type Day, type Funnel, type Step } from './funnelData'
import { EmailFunnel, PageTypes } from './EmailFunnel'
import { useGrowth, useInternal } from './parts'

export function FunnelPage() {
  const [params, setParams] = useSearchParams()
  const [internal, box] = useInternal()
  const days = params.get('days') ?? ''
  const q = useQuery({
    queryKey: ['staff', 'funnel', days, internal],
    queryFn: () => api<Funnel>('GET', `/api/staff/funnel?days=${days}${internal ? '&internal=1' : ''}`),
    refetchInterval: 300_000,
  })
  const setDays = (d: string) => { const next = new URLSearchParams(params); next.set('days', d); setParams(next, { replace: true }) }
  const d = q.data
  const items = [{ key: '7', label: '7 days' }, { key: '30', label: '30 days' }, { key: '90', label: '90 days' }, { key: 'all', label: 'All' }, ...(d?.visits_window ? [{ key: 'visits', label: 'Days with page views' }] : [])]
  return (
    <Page title="Funnel" testid="staff-funnel" sub={d ? <>{d.from ? `${short(d.from)} to ${short(d.to)}` : `All time to ${short(d.to)}`}, Pacific days <Fresh at={d.at} live testid="funnel-read" /></> : undefined}
      right={<><Strip testid="funnel-days" value={d?.days ?? days} onChange={setDays} items={items} />{box}</>}>
      {q.isLoading ? <Busy what="Reading the funnel" /> : q.error ? <Err error={q.error} /> : d ? <Body d={d} setDays={setDays} internal={internal} /> : null}
    </Page>
  )
}

function Body({ d, setDays, internal }: { d: Funnel; setDays: (v: string) => void; internal: boolean }) {
  const [empty, setEmpty] = useState(false)
  const c = d.columns
  const hidden = d.rows.filter((r) => r.empty).length
  const rows = empty ? d.rows : d.rows.filter((r) => !r.empty)
  return (
    <>
      <Answer a={d.answer} />
      <Section title="By channel" testid="funnel-channels" right={hidden ? <Button tone="quiet" onClick={() => setEmpty((v) => !v)} data-testid="funnel-show-empty">{empty ? 'Hide empty' : `Show empty (${hidden})`}</Button> : undefined}>
        <div className="st-fn-flat"><Table flat rows={[...rows, d.total]} columns={funnelColumns(d, 'Channel')} rowKey={(r) => r.key} testid="funnel-table" /></div>
        <p className="st-note st-fn-phone" data-testid="funnel-rates">All channels: signups per page view {pct(d.total.visit_signup)}, signups who sent {pct(d.total.signup_sent)}</p>
        <p className="st-note" data-testid="funnel-visits-note">
          {!d.visits_read ? "Page views by channel aren't connected. They come from Ploy analytics." : c.visits.covers ? null : <>Page views are grey because they cover {d.visits_window ? range(d.visits_window.from, d.visits_window.to) : 'other days'}, not these. {d.visits_window ? <Button tone="quiet" onClick={() => setDays('visits')} data-testid="funnel-use-visits">Use those days</Button> : null}</>}{' '}
          Signups who sent counts only people who signed up in these days.
          {d.calls_check ? ` Outreach counts ${d.calls_check.outreach} cold email calls, this table ${d.calls_check.here}.` : ''}
        </p>
      </Section>
      <Daily rows={d.daily} capped={d.chart_capped} />
      <Stuck steps={d.stuck} />
      <Details d={d} internal={internal} />
      <details className="st-td-details" data-testid="funnel-sources">
        <summary>Sources</summary>
        <Sources>
          <Fresh at={c.visits.read_at} label={`Page views, ${c.visits.from ? range(c.visits.from, c.visits.to) : 'days not read'},`} limit={c.visits.ok ? undefined : 0} testid="funnel-fresh-visits" />
          <Fresh at={c.calls.read_at} label={`Calls, ${range(c.calls.from, c.calls.to)},`} limit={c.calls.ok ? undefined : 0} testid="funnel-fresh-calls" />
          <Fresh at={d.at} label="Signups and card requests" live testid="funnel-fresh-app" />
        </Sources>
      </details>
    </>
  )
}

/** The question this page exists for: where did this week's requests come from. Tags first, then what our own records show, marked as that. */
function Answer({ a }: { a: Funnel['answer'] }) {
  return (
    <div className="st-stack" data-testid="funnel-answer">
      <p className="st-td-verdict">{a.text}</p>
      {a.note ? <p className="st-note" data-testid="funnel-answer-note">{a.note}</p> : null}
      {a.rows.length ? (
        <Table rows={a.rows} rowKey={(r) => r.key} testid="funnel-requests" columns={[
          { key: 'company', head: 'Company', primary: true, cell: (r) => <Link className="st-link" to={r.to}>{r.title}</Link> },
          { key: 'source', head: 'Source', cell: (r) => <span className={r.tagged ? undefined : 'st-muted'}>{r.source}</span> },
          { key: 'why', head: 'How we know', cell: (r) => <span className="st-muted">{r.why}</span> },
          { key: 'state', head: 'Request', cell: (r) => r.sub },
          { key: 'at', head: 'When', num: true, cell: (r) => ago(r.since) },
        ]} />
      ) : null}
    </div>
  )
}

/** Page types and the email funnel, folded and read only when opened. There is one funnel, and this is its small print. */
function Details({ d, internal }: { d: Funnel; internal: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <details className="st-td-details" data-testid="funnel-details" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>Page types and email detail</summary>
      {open ? <More d={d} internal={internal} /> : null}
    </details>
  )
}

function More({ d, internal }: { d: Funnel; internal: boolean }) {
  const g = useGrowth(internal)
  return (
    <div className="st-stack st-fn-email">
      <PageTypes d={d} />
      {g.isLoading ? <Busy what="Reading outreach" /> : g.error ? <Err error={g.error} /> : g.data ? <EmailFunnel g={g.data} drafts={d.open.drafts} /> : null}
    </div>
  )
}

const MEASURES: { key: keyof Day; label: string; unit: string }[] = [
  { key: 'visitors', label: 'Site visitors', unit: 'visitors' }, { key: 'signups', label: 'Signups', unit: 'signups' }, { key: 'started', label: 'Card started', unit: 'started' },
  { key: 'sent', label: 'Card sent', unit: 'sent' }, { key: 'booked', label: 'Calls booked', unit: 'calls' },
]

/** One small chart per measure, because they sit on different scales and two scales on one axis mislead. A day nobody read is
 * a faint tick at the baseline, not a zero. Hover or tap a day to read every measure for it. */
function Daily({ rows, capped }: { rows: Day[]; capped: boolean }) {
  const [hover, setHover] = useState<number | null>(null)
  const day = hover === null ? null : rows[hover]
  return (
    <Section title="Daily" testid="funnel-daily" right={<span className="st-note" data-testid="funnel-daily-read">{day
      ? `${short(day.day)}: ${MEASURES.map((m) => `${n(day[m.key] as Num)} ${m.unit}`).join(', ')}`
      : capped ? 'Last 91 days. Hover or tap a day' : 'Hover or tap a day'}</span>}>
      <div className="st-fn-multiples" onMouseLeave={() => setHover(null)}>
        {MEASURES.map((m) => <Multiple key={m.key} rows={rows} m={m} hover={hover} setHover={setHover} />)}
      </div>
    </Section>
  )
}

function Multiple({ rows, m, hover, setHover }: { rows: Day[]; m: typeof MEASURES[number]; hover: number | null; setHover: (i: number | null) => void }) {
  const W = 240, H = 64, base = H - 1
  const vals = rows.map((r) => r[m.key] as Num)
  const max = Math.max(1, ...vals.map((v) => v ?? 0))
  const total = sum(vals)
  const band = W / Math.max(rows.length, 1)
  const bar = Math.max(1, Math.min(band - 1, 14))
  return (
    <div className="st-fn-multiple" data-testid={`funnel-daily-${m.key}`}>
      <div className="st-fn-multiple-head"><span>{m.label}</span><span className="st-strong">{n(total)}</span></div>
      <svg className="st-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${m.label} a day, ${n(total)} in all`}>
        <line x1={0} x2={W} y1={base} y2={base} className="st-chart-grid" />
        {vals.map((v, i) => {
          const x = i * band + (band - bar) / 2
          const h = v ? Math.max(2, (v / max) * (base - 4)) : 0
          return (
            <g key={rows[i].day} onMouseEnter={() => setHover(i)} onClick={() => setHover(i)}>
              <title>{`${short(rows[i].day)}: ${v === null ? 'not read' : n(v)}`}</title>
              <rect x={i * band} y={0} width={band} height={H} fill="transparent" />
              {v === null ? <rect x={x} y={base - 1} width={bar} height={1} className="st-tick-off" />
                : h ? <rect x={x} y={base - h} width={bar} height={h} rx={Math.min(2, bar / 2)} className={hover === i ? 'st-mark' : 'st-mark-mid'} /> : null}
            </g>
          )
        })}
      </svg>
      {rows.length ? <div className="st-fn-dates"><span>{short(rows[0].day)}</span><span>{short(rows[rows.length - 1].day)}</span></div> : null}
    </div>
  )
}

function Stuck({ steps }: { steps: Step[] }) {
  return (
    <Section title="Who's stuck at each step" testid="funnel-stuck">
      <div className="st-fn-steps">
        {steps.map((s) => (
          <div key={s.key} data-testid={`funnel-stuck-${s.key}`}>
            <h3 className="st-fn-step">{s.label}<span>{s.total}</span></h3>
            {s.rows.length === 0 ? <p className="st-note">Nobody in this window</p> : s.rows.map((r) => (
              <div key={r.key} className="st-fn-who">
                <Link className="st-link" to={r.to}>{r.title}</Link><span className="st-faint" title={r.since}>{ago(r.since)}</span>
              </div>
            ))}
            {s.total > s.rows.length ? <p className="st-note">And {s.total - s.rows.length} more</p> : null}
          </div>
        ))}
      </div>
    </Section>
  )
}
